/**
 * MobileProfileList
 *
 * Phone replacement for the 7-column PrimeReact profiles table. The table needs
 * ~1340px of width, so on a 360px screen it is either a sideways-scrolling strip
 * or it pushes the page off-canvas; one card per profile keeps every field
 * readable in a single column.
 *
 * Presentational only — every mutation is handed back to ProfileManagementTab,
 * which owns the Redux dispatches.
 */
import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import EditIcon from "@mui/icons-material/Edit";
import SecurityIcon from "@mui/icons-material/Security";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Langs, { Localize } from "../../../app/lang/langs";
import { IsManagerLabel } from "../ProfileModal";

const CARD_SX = {
    mb: 1.5,
    borderRadius: "16px",
    bgcolor: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(0, 40, 128, 0.08)",
    boxShadow: "0 1px 2px rgba(0, 40, 128, 0.04), 0 4px 12px rgba(0, 40, 128, 0.05)",
};

const statusChipSx = (ok) => ({
    height: 26,
    fontWeight: 600,
    fontSize: "0.75rem",
    borderRadius: "8px",
    bgcolor: ok ? "rgba(58, 156, 96, 0.12)" : "rgba(211, 47, 47, 0.10)",
    color: ok ? "rgb(40, 120, 74)" : "rgb(176, 32, 32)",
});

function matchesFilter(profile, filter) {
    if (!filter) return true;
    const needle = filter.toLowerCase();
    const haystack = [
        profile.name,
        profile.description,
        ...(profile.teams || []).map((t) => t.name),
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    return haystack.includes(needle);
}

export default function MobileProfileList({
    profiles,
    filter,
    lockedProfileIds = [],
    onEdit,
    onViewPermissions,
    onToggleIsManager,
}) {
    const visible = (profiles || []).filter((p) => matchesFilter(p, filter));

    if (!visible.length) {
        return (
            <Box
                sx={{
                    py: 4,
                    textAlign: "center",
                    color: "rgba(0, 40, 128, 0.5)",
                    fontSize: "0.9rem",
                }}
            >
                <Langs str="NO_RESULTS" />
            </Box>
        );
    }

    return (
        <Box>
            {visible.map((profile) => {
                const isLocked = lockedProfileIds.includes(profile.id);
                // Rows fetched from a server without the column report no
                // `displayable`; show those as displayable rather than as hidden.
                const shown = profile.displayable === undefined ? true : !!profile.displayable;

                return (
                    <Card key={profile.id} elevation={0} sx={CARD_SX}>
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: "1rem",
                                        color: "rgb(0, 40, 128)",
                                        minWidth: 0,
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    {profile.name}
                                </Typography>
                                {isLocked && (
                                    <LockOutlinedIcon
                                        titleAccess={Localize("PROFILE_LOCKED")}
                                        sx={{ fontSize: 16, color: "rgba(0, 40, 128, 0.4)", flexShrink: 0 }}
                                    />
                                )}
                            </Box>

                            {profile.description && (
                                <Typography
                                    sx={{
                                        fontSize: "0.85rem",
                                        color: "rgba(0, 40, 128, 0.6)",
                                        mb: 1,
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    {profile.description}
                                </Typography>
                            )}

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.25 }}>
                                <Chip
                                    label={<Langs str={profile.active ? "active" : "inactive"} />}
                                    size="small"
                                    sx={statusChipSx(!!profile.active)}
                                />
                                <Chip
                                    label={<Langs str={shown ? "PROFILE_DISPLAYABLE" : "PROFILE_HIDDEN"} />}
                                    size="small"
                                    sx={statusChipSx(shown)}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    color: "rgba(0, 40, 128, 0.45)",
                                    mb: 0.5,
                                }}
                            >
                                <Langs str="TEAMS" />
                            </Typography>
                            {/* Every team is listed: the desktop hides the overflow behind a
                                hover tooltip, which nothing on a touch screen can open. */}
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                                {profile.teams && profile.teams.length ? (
                                    profile.teams.map((t) => (
                                        <Chip
                                            key={t.id}
                                            label={t.name}
                                            size="small"
                                            sx={{
                                                height: 26,
                                                fontSize: "0.75rem",
                                                fontWeight: 500,
                                                borderRadius: "8px",
                                                bgcolor: "rgba(255, 144, 21, 0.12)",
                                                color: "#c96f00",
                                            }}
                                        />
                                    ))
                                ) : (
                                    <Chip
                                        label={<Langs str="ALL_TEAMS" />}
                                        size="small"
                                        sx={{
                                            height: 26,
                                            fontSize: "0.75rem",
                                            fontWeight: 600,
                                            borderRadius: "8px",
                                            bgcolor: "rgba(0, 40, 128, 0.08)",
                                            color: "rgb(0, 40, 128)",
                                        }}
                                    />
                                )}
                            </Box>

                            <Divider sx={{ my: 1, borderColor: "rgba(0, 40, 128, 0.06)" }} />

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    minHeight: 48,
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        fontSize: "0.9rem",
                                        fontWeight: 600,
                                        color: "rgb(0, 40, 128)",
                                    }}
                                >
                                    <IsManagerLabel />
                                </Box>
                                <Switch
                                    checked={!!profile.is_manager}
                                    onChange={(e) => onToggleIsManager(profile, e.target.checked)}
                                    inputProps={{ "aria-label": profile.name }}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#FF9015" },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                            backgroundColor: "#FF9015",
                                        },
                                    }}
                                />
                            </Box>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 1.5 }}>
                                {!isLocked && (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<EditIcon />}
                                        onClick={() => onEdit(profile)}
                                        sx={{
                                            minHeight: 48,
                                            borderRadius: "12px",
                                            textTransform: "none",
                                            fontWeight: 600,
                                            fontSize: "0.95rem",
                                            borderColor: "rgba(255, 144, 21, 0.5)",
                                            color: "#c96f00",
                                        }}
                                    >
                                        <Langs str="edit" />
                                    </Button>
                                )}
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<SecurityIcon />}
                                    onClick={() => onViewPermissions && onViewPermissions(profile.name)}
                                    sx={{
                                        minHeight: 48,
                                        borderRadius: "12px",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "0.95rem",
                                        borderColor: "rgba(0, 40, 128, 0.35)",
                                        color: "rgb(0, 40, 128)",
                                    }}
                                >
                                    <Langs str="VIEW_PERMISSIONS" />
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                );
            })}
        </Box>
    );
}
